<template>
  <LcHighFrequencyTeaser
    v-bind="{
      backgroundPattern,
      badge,
      headline,
      jackpot,
      primaryButtonProps,
      ballConfig,
      hint,
    }"
    :style="theme"
    :variant="lotteryTeaserVariant === 'expressive' ? 'default' : lotteryTeaserVariant"
    @primary-button-click="addBonusCode"
    @teaser-click="addBonusCode"
  >
    <template #lottery-logo>
      <LcLotteryLogo v-bind="{ licensedTerritory, lotteryId }" size="fluid" />
    </template>

    <template #hint>
      <i18n-t scope="global" :keypath="hintKey">
        <template v-if="operatingHours" #time>
          <NuxtTime :datetime="operatingHours.nextStart" :hour12="true" time-style="short" />
        </template>
      </i18n-t>
    </template>
  </LcHighFrequencyTeaser>
</template>

<script lang="ts" setup>
import type {
  LcHighFrequencyBallConfig,
  LcLotteryTeaserVariant,
} from '@lottocom/frontend-components'

import type {
  CampaignPayload,
  JackpotType,
  LotteryId,
  Money,
  OperatingHoursRange,
  QuickPickConfiguration,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

export type HighFrequencyTeaserProps = {
  campaign?: Maybe<CampaignPayload>
  id: string
  jackpotSize: Money
  jackpotType: JackpotType
  licensedTerritory: string
  operating?: boolean
  operatingHours?: OperatingHoursRange
  quickPick?: Maybe<Pick<QuickPickConfiguration, 'plays'>>
  variant?: LcLotteryTeaserVariant
}

const BASE_BALL_CONFIG: LcHighFrequencyBallConfig[] = [
  { left: '40%', size: 'medium', top: '2%' },
  { left: '97%', size: 'medium', top: '35%' },
  { left: '2%', size: 'medium', top: '45%' },
  { left: '20%', size: 'medium', top: '100%' },
  { left: '82%', size: 'medium', top: '90%' },
]

const {
  campaign,
  id,
  jackpotSize,
  jackpotType,
  licensedTerritory,
  operating,
  operatingHours,
  variant,
} = defineProps<HighFrequencyTeaserProps>()

const { t } = useI18nUtils()

const route = useRoute()

const ballConfig = useState(`${id}-HighFrequencyTeaserBallConfig`, () =>
  BASE_BALL_CONFIG.map((option) => ({
    ...option,
    content: Math.floor(Math.random() * 50 + 10).toString(),
  })),
)

const headline = computed(() =>
  jackpotType === 'FIXED' ? t('lottery.teaser.headline.fixed') : t('lottery.teaser.headline'),
)

const jackpot = computed(() => ({
  ...jackpotSize,
}))

const lotteryId = computed(() => id as LotteryId)

const primaryButtonProps = computed(() => ({
  ariaLabel: t('lottery.teaser.button.playnow.arialabel', {
    lottery: t(`lottery.name.${id}`),
  }),
  text: t('lottery.teaser.button.playnow'),
  to: {
    path: `/keno/${id.toLowerCase()}`,
  },
}))

const hintKey = computed(() =>
  operating
    ? 'highfrequencygame.teaser.hint.operating'
    : 'highfrequencygame.teaser.hint.notoperating.dynamic',
)
const hint = computed(() => {
  if (operating) {
    return t('highfrequencygame.teaser.hint.operating')
  }

  if (!operatingHours) return

  return t('highfrequencygame.teaser.hint.notoperating.dynamic', {
    time: formatDate(operatingHours.nextStart, 'drawingTime'),
  })
})

const theme = computed(() => {
  const { createCssVar } = getLotteryCssVarConstructor(licensedTerritory, lotteryId.value)

  return {
    '--high-frequency-ball-background': createCssVar('high-frequency-ball-background'),
    '--high-frequency-ball-color': createCssVar('high-frequency-ball-text-color'),
    '--high-frequency-game-background-color': createCssVar(
      'high-frequency-teaser-background-color',
    ),
    '--high-frequency-game-background-gradient': createCssVar(
      'high-frequency-teaser-background-gradient',
    ),
    '--lottery-jackpot-color': createCssVar('high-frequency-teaser-jackpot-color'),
    '--lottery-jackpot-text-color': createCssVar('high-frequency-teaser-text-color'),
    '--teaser-frame-color': createCssVar('color-accent'),
  }
})

const { addBonusCode, backgroundPattern, badge, lotteryTeaserVariant } = useOnsiteMarketingTeaser(
  toRef(() => campaign),
  toRef(() => variant),
)

useHighFrequencyTeaserRefetch(operating, operatingHours)
</script>
